/**
 * Trainer card export utility.
 * Composes a trainer card image into a canvas and exports as either
 * a static PNG or an animated APNG.
 *
 * APNG construction is done in JavaScript using the minimal PNG/APNG chunk
 * format so it works in the browser without extra dependencies. Only the trainer
 * sprite is animated; if the trainer asset is a static image, the resulting APNG
 * will still play but only contains one frame.
 */

import type { TrainerProfile } from "../../engine/types";
import { trainerFrontUrl, trainerBackUrl, trainerVsUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";

export type CardFormat = "static" | "animated";

const CARD_WIDTH = 320;
const CARD_HEIGHT = 192;
const FRAME_DELAY_NUM = 1; // numerator
const FRAME_DELAY_DEN = 2; // denominator => 0.5s per frame (2 fps)

export async function exportTrainerCard(profile: TrainerProfile, format: CardFormat): Promise<void> {
  const canvas = document.createElement("canvas");
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to create canvas context");

  const bgColors = ["#1a1a2e", "#252544", "#3a3a5c"];
  const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  gradient.addColorStop(0, bgColors[0]);
  gradient.addColorStop(0.5, bgColors[1]);
  gradient.addColorStop(1, bgColors[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  ctx.strokeStyle = "#5a8fd8";
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, CARD_WIDTH - 4, CARD_HEIGHT - 4);

  ctx.fillStyle = "#e8e8f0";
  ctx.font = 'bold 22px "Segoe UI", system-ui, sans-serif';
  ctx.fillText(profile.name, 16, 36);

  ctx.fillStyle = "#a0a0c0";
  ctx.font = '14px "Segoe UI", system-ui, sans-serif';
  ctx.fillText("Pokemon Battle Simulator", 16, 58);

  const frontUrl = trainerFrontUrl(profile.frontSprite);
  const vsUrl = trainerVsUrl(profile.vsSprite);
  const backUrl = trainerBackUrl(profile.backSprite);

  const resolvedFront = await resolveSpriteUrl(frontUrl);
  const resolvedVs = await resolveSpriteUrl(vsUrl);
  const resolvedBack = await resolveSpriteUrl(backUrl);

  const frontImg = resolvedFront ? await loadImage(resolvedFront) : null;
  const vsImg = resolvedVs ? await loadImage(resolvedVs) : null;
  const backImg = resolvedBack ? await loadImage(resolvedBack) : null;

  if (format === "animated" && frontImg && isAnimatedSprite(resolvedFront!)) {
    await exportAnimatedPng(canvas, ctx, profile, frontImg, vsImg, backImg);
    return;
  }

  drawCardContents(ctx, frontImg, vsImg, backImg, true);
  const blob = await canvasToBlob(canvas, "image/png");
  downloadBlob(blob, `trainer-card-${profile.name}.png`);
}

function drawCardContents(
  ctx: CanvasRenderingContext2D,
  frontImg: HTMLImageElement | null,
  vsImg: HTMLImageElement | null,
  backImg: HTMLImageElement | null,
  fullOpacity: boolean,
): void {
  ctx.save();
  if (!fullOpacity) ctx.globalAlpha = 0;

  if (frontImg) {
    const size = 96;
    ctx.drawImage(frontImg, CARD_WIDTH - size - 16, 16, size, size);
  }
  if (vsImg) {
    const size = 64;
    ctx.drawImage(vsImg, 16, CARD_HEIGHT - size - 16, size, size);
  }
  if (backImg) {
    const size = 80;
    ctx.drawImage(backImg, CARD_WIDTH - size - 16, CARD_HEIGHT - size - 16, size, size);
  }

  ctx.restore();
}

async function exportAnimatedPng(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  profile: TrainerProfile,
  frontImg: HTMLImageElement,
  vsImg: HTMLImageElement | null,
  backImg: HTMLImageElement | null,
): Promise<void> {
  // We only animate the front sprite. If it is a GIF, we cannot trivially encode
  // it to APNG here, so we fall back to a static PNG when the resolved format is GIF.
  const url = trainerFrontUrl(profile.frontSprite);
  if (url.endsWith(".gif") || (frontImg.src && frontImg.src.endsWith(".gif"))) {
    drawCardContents(ctx, frontImg, vsImg, backImg, true);
    const blob = await canvasToBlob(canvas, "image/png");
    downloadBlob(blob, `trainer-card-${profile.name}.png`);
    return;
  }

  const frames = await extractApngFrames(url, frontImg);
  if (frames.length <= 1) {
    drawCardContents(ctx, frontImg, vsImg, backImg, true);
    const blob = await canvasToBlob(canvas, "image/png");
    downloadBlob(blob, `trainer-card-${profile.name}.png`);
    return;
  }

  const frameData: Uint8Array[] = [];
  for (const frame of frames) {
    // Clear to base card without the animated sprite
    ctx.clearRect(0, 0, CARD_WIDTH, CARD_HEIGHT);
    drawCardBase(ctx);
    drawCardContents(ctx, null, vsImg, backImg, true);
    ctx.drawImage(frame, CARD_WIDTH - 96 - 16, 16, 96, 96);
    const data = ctx.getImageData(0, 0, CARD_WIDTH, CARD_HEIGHT).data;
    frameData.push(pngEncode(CARD_WIDTH, CARD_HEIGHT, data));
  }

  const apng = buildApng(frameData, CARD_WIDTH, CARD_HEIGHT, FRAME_DELAY_NUM, FRAME_DELAY_DEN);
  const blob = new Blob([apng.buffer as ArrayBuffer], { type: "image/png" });
  downloadBlob(blob, `trainer-card-${profile.name}.png`);
}

function drawCardBase(ctx: CanvasRenderingContext2D): void {
  const bgColors = ["#1a1a2e", "#252544", "#3a3a5c"];
  const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  gradient.addColorStop(0, bgColors[0]);
  gradient.addColorStop(0.5, bgColors[1]);
  gradient.addColorStop(1, bgColors[2]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  ctx.strokeStyle = "#5a8fd8";
  ctx.lineWidth = 4;
  ctx.strokeRect(2, 2, CARD_WIDTH - 4, CARD_HEIGHT - 4);
}

async function extractApngFrames(url: string, img: HTMLImageElement): Promise<HTMLImageElement[]> {
  // If the resolved URL is a PNG, check if it actually contains APNG chunks.
  if (!url.endsWith(".png") && !url.endsWith(".apng")) return [img];
  try {
    const res = await fetch(url);
    const arrayBuffer = await res.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    if (!hasApngChunks(bytes)) return [img];
    return decodeApngFrames(bytes);
  } catch {
    return [img];
  }
}

function hasApngChunks(bytes: Uint8Array): boolean {
  for (let i = 8; i < bytes.length - 8;) {
    const len = readUint32(bytes, i);
    const type = String.fromCharCode(...bytes.slice(i + 4, i + 8));
    if (type === "acTL") return true;
    if (type === "IEND") return false;
    i += 12 + len;
  }
  return false;
}

async function decodeApngFrames(bytes: Uint8Array): Promise<HTMLImageElement[]> {
  // Simple APNG decoder: extract each frame as a separate PNG by replacing the
  // frame data chunks with a single IDAT and reconstructing the PNG.
  const frames: HTMLImageElement[] = [];
  const chunks: { type: string; data: Uint8Array }[] = [];
  for (let i = 8; i < bytes.length - 8;) {
    const len = readUint32(bytes, i);
    const type = String.fromCharCode(...bytes.slice(i + 4, i + 8));
    const data = bytes.slice(i + 8, i + 8 + len);
    chunks.push({ type, data });
    i += 12 + len;
  }

  let frameCount = 0;
  for (const c of chunks) {
    if (c.type === "fcTL") frameCount++;
  }
  if (frameCount === 0) {
    // Single static image wrapped in APNG chunks
    return [await loadImageFromBytes(bytes)];
  }

  // Build a separate PNG for each frame (dispose/blend ignored; each frame is a full image).
  const common: { type: string; data: Uint8Array }[] = [];
  let currentFrameIdat: Uint8Array | null = null;
  for (const c of chunks) {
    if (c.type === "IHDR") common.push(c);
    else if (c.type === "sRGB" || c.type === "gAMA" || c.type === "cHRM" || c.type === "pHYs") common.push(c);
    else if (c.type === "PLTE" || c.type === "tRNS") common.push(c);
    else if (c.type === "fcTL") {
      if (currentFrameIdat) {
        frames.push(await buildPngFromChunks(common, currentFrameIdat));
      }
      currentFrameIdat = null;
    } else if (c.type === "fdAT") {
      const idatData = c.data.slice(4); // skip sequence number
      currentFrameIdat = currentFrameIdat ? concatenateUint8(currentFrameIdat, idatData) : idatData;
    } else if (c.type === "IDAT") {
      currentFrameIdat = currentFrameIdat ? concatenateUint8(currentFrameIdat, c.data) : c.data;
    }
  }
  if (currentFrameIdat) {
    frames.push(await buildPngFromChunks(common, currentFrameIdat));
  }
  return frames;
}

async function buildPngFromChunks(common: { type: string; data: Uint8Array }[], idat: Uint8Array): Promise<HTMLImageElement> {
  const out: number[] = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  for (const c of common) writeChunk(out, c.type, c.data);
  writeChunk(out, "IDAT", idat);
  writeChunk(out, "IEND", new Uint8Array(0));
  const blob = new Blob([new Uint8Array(out)], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  return loadImage(url);
}

function writeChunk(out: number[], type: string, data: Uint8Array): void {
  const len = data.length;
  writeUint32(out, len);
  const typeBytes: number[] = [];
  for (let i = 0; i < 4; i++) typeBytes.push(type.charCodeAt(i));
  const crcStart = out.length;
  for (const b of typeBytes) out.push(b);
  for (const b of data) out.push(b);
  const crc = crc32(new Uint8Array(out.slice(crcStart)));
  writeUint32(out, crc);
}

function buildApng(frames: Uint8Array[], width: number, height: number, delayNum: number, delayDen: number): Uint8Array {
  const out: number[] = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  writeIhdr(out, width, height);

  const acTL = new Uint8Array(8);
  writeUint32To(acTL, frames.length, 0);
  writeUint32To(acTL, 0, 4); // loop count 0 = infinite
  writeChunk(out, "acTL", acTL);

  for (let i = 0; i < frames.length; i++) {
    const framePng = frames[i];
    const chunks = parsePngChunks(framePng);
    let idat: Uint8Array | null = null;
    for (const c of chunks) {
      if (c.type === "IDAT") {
        idat = idat ? concatenateUint8(idat, c.data) : c.data;
      }
    }
    if (!idat) continue;

    const fcTL = new Uint8Array(26);
    let offset = 0;
    writeUint32To(fcTL, i, offset); offset += 4; // sequence_number
    writeUint32To(fcTL, width, offset); offset += 4; // width
    writeUint32To(fcTL, height, offset); offset += 4; // height
    writeUint32To(fcTL, 0, offset); offset += 4; // x_offset
    writeUint32To(fcTL, 0, offset); offset += 4; // y_offset
    writeUint16To(fcTL, delayNum, offset); offset += 2; // delay_num
    writeUint16To(fcTL, delayDen, offset); offset += 2; // delay_den
    fcTL[offset] = 0; // dispose_op
    fcTL[offset + 1] = 0; // blend_op
    writeChunk(out, "fcTL", fcTL);

    const fdAT = new Uint8Array(4 + idat.length);
    writeUint32To(fdAT, i, 0); // sequence_number
    fdAT.set(idat, 4);
    writeChunk(out, "fdAT", fdAT);
  }

  writeChunk(out, "IEND", new Uint8Array(0));
  return new Uint8Array(out);
}

function parsePngChunks(bytes: Uint8Array): { type: string; data: Uint8Array }[] {
  const chunks: { type: string; data: Uint8Array }[] = [];
  for (let i = 8; i < bytes.length - 8;) {
    const len = readUint32(bytes, i);
    const type = String.fromCharCode(...bytes.slice(i + 4, i + 8));
    const data = bytes.slice(i + 8, i + 8 + len);
    chunks.push({ type, data });
    i += 12 + len;
  }
  return chunks;
}

function writeIhdr(out: number[], width: number, height: number): void {
  const data = new Uint8Array(13);
  writeUint32To(data, width, 0);
  writeUint32To(data, height, 4);
  data[8] = 8; // bit depth
  data[9] = 6; // color type: RGBA
  data[10] = 0; // compression
  data[11] = 0; // filter
  data[12] = 0; // interlace
  writeChunk(out, "IHDR", data);
}

function pngEncode(width: number, height: number, rgba: Uint8ClampedArray): Uint8Array {
  const rowLen = width * 4;
  const raw: number[] = [];
  for (let y = 0; y < height; y++) {
    raw.push(0); // filter type: none
    for (let x = 0; x < width; x++) {
      const idx = y * rowLen + x * 4;
      raw.push(rgba[idx], rgba[idx + 1], rgba[idx + 2], rgba[idx + 3]);
    }
  }
  const compressed = deflateSync(new Uint8Array(raw));
  const out: number[] = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  writeIhdr(out, width, height);
  writeChunk(out, "IDAT", compressed);
  writeChunk(out, "IEND", new Uint8Array(0));
  return new Uint8Array(out);
}

function deflateSync(data: Uint8Array): Uint8Array {
  // Use native CompressionStream if available.
  if (typeof CompressionStream !== "undefined") {
    // CompressionStream is async; we cannot use it synchronously. Fall back to a simple
    // non-compressed deflate implementation below.
  }
  return simpleDeflate(data);
}

function simpleDeflate(data: Uint8Array): Uint8Array {
  // Minimal zlib/deflate encoder producing non-compressed (stored) blocks.
  // This is larger but trivially correct and avoids implementing fixed Huffman codes.
  const out: number[] = [0x78, 0x9c]; // zlib header: deflate, 32K window
  let pos = 0;
  while (pos < data.length) {
    const blockSize = Math.min(data.length - pos, 65535);
    const bfinal = pos + blockSize >= data.length ? 1 : 0;
    out.push(bfinal); // BFINAL + BTYPE=00 (stored)
    // Align to byte boundary (stored blocks start on byte boundary).
    // LEN and NLEN are little-endian 16-bit.
    const len = blockSize;
    const nlen = ~len & 0xffff;
    out.push(len & 0xff, (len >>> 8) & 0xff, nlen & 0xff, (nlen >>> 8) & 0xff);
    for (let i = 0; i < blockSize; i++) {
      out.push(data[pos + i]);
    }
    pos += blockSize;
  }
  const adler = adler32(data);
  writeUint32(out, adler);
  return new Uint8Array(out);
}

function adler32(data: Uint8Array): number {
  let a = 1, b = 0;
  for (const byte of data) {
    a = (a + byte) % 65521;
    b = (b + a) % 65521;
  }
  return (b << 16) | a;
}

function crc32(bytes: Uint8Array): number {
  const table = makeCrc32Table();
  let crc = 0xffffffff;
  for (const b of bytes) {
    crc = table[(crc ^ b) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

let crcTable: Uint32Array | null = null;
function makeCrc32Table(): Uint32Array {
  if (crcTable) return crcTable;
  crcTable = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
    }
    crcTable[n] = c >>> 0;
  }
  return crcTable;
}

function readUint32(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;
}

function writeUint32(out: number[], value: number): void {
  out.push((value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff);
}

function writeUint32To(out: Uint8Array, value: number, offset: number): void {
  out[offset] = (value >>> 24) & 0xff;
  out[offset + 1] = (value >>> 16) & 0xff;
  out[offset + 2] = (value >>> 8) & 0xff;
  out[offset + 3] = value & 0xff;
}

function writeUint16To(out: Uint8Array, value: number, offset: number): void {
  out[offset] = (value >>> 8) & 0xff;
  out[offset + 1] = value & 0xff;
}

function concatenateUint8(a: Uint8Array, b: Uint8Array): Uint8Array {
  const result = new Uint8Array(a.length + b.length);
  result.set(a, 0);
  result.set(b, a.length);
  return result;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

function loadImageFromBytes(bytes: Uint8Array): Promise<HTMLImageElement> {
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: "image/png" });
  return loadImage(URL.createObjectURL(blob));
}

function isAnimatedSprite(src: string): boolean {
  return src.endsWith(".gif") || src.endsWith(".apng") || src.endsWith(".png");
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas toBlob failed"));
    }, type);
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
